import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';

const ForgotPassword = ({ onConfirm, onClose }) => {
  const { loggedUser } = useContext(AuthContext); // might not be used here — often null in forgot pw flow

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [question, setQuestion] = useState(null);        // { question_id, question }
  const [answer, setAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('enter-username');    // 'enter-username' → 'answer-question' → 'set-password'

  // Auto-fetch random question when username is exactly 10 chars
  useEffect(() => {
    if (username.length === 10) {
      fetchRandomUserQuestion();
    } else {
      // Reset when username changes / shortened
      setQuestion(null);
      setAnswer('');
      setStep('enter-username');
    }
  }, [username]);

  const fetchRandomUserQuestion = async () => {
    try {
      setMessage('');
      const res = await fetch(`/api/forgot-password/question?username=${encodeURIComponent(username)}`);
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not retrieve security question');
      }

      const data = await res.json();
      if (!data.question_id) {
        throw new Error('No security questions found for this user');
      }

      setQuestion(data);           // { question_id, question }
      setStep('answer-question');
    } catch (err) {
      setMessage(err.message);
      setQuestion(null);
      setStep('enter-username');
    }
  };

  const verifyAnswerAndResetPassword = async () => {
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return false;
    }

    if (password.length < 8) { // example rule — adjust as needed
      setMessage('Password must be at least 8 characters');
      return false;
    }

    try {
      const payload = {
        username,
        answer,
        questionId: question.question_id,
        newPassword: password,
      };

      const res = await fetch('/api/forgot-password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Verification or reset failed');
      }

      setMessage('Password reset successful!');
      return true;
    } catch (err) {
      setMessage(err.message);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    let success = false;

    if (step === 'enter-username') {
      // Should not reach here — button disabled unless length===10
    } else if (step === 'answer-question') {
      if (!answer.trim()) {
        setMessage('Please provide an answer');
        setIsSubmitting(false);
        return;
      }
      // Move to password step if they want — or verify + reset in one go
      setStep('set-password');
    } else if (step === 'set-password') {
      success = await verifyAnswerAndResetPassword();
      if (success) {
        onConfirm?.(); // close modal / redirect
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div className="login-form">
      <div className="center">
        <h1>Forgot Password</h1>
        <p>
          {step === 'enter-username' && 'Enter your 10-digit customer number'}
          {step === 'answer-question' && 'Answer your security question'}
          {step === 'set-password' && 'Set your new password'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="pw-service-form">
        {/* Username – always visible */}
        <div>
          <input
            type="text"
            placeholder="Customer Number"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\D/g, ''))} // only digits
            maxLength={10}
            required
          />
        </div>

        {/* Question + Answer – shown after fetch */}
        {question && step !== 'set-password' && (
          <div>
            <label>{question.question}</label>
            <input
              type="text"
              placeholder="Your answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              required
            />
          </div>
        )}

        {/* Password fields – shown after correct answer step */}
        {step === 'set-password' && (
          <>
            <div>
              <input
                type="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={isSubmitting || (step === 'enter-username' && username.length !== 10)}
        >
          {isSubmitting ? 'Processing...' : step === 'set-password' ? 'RESET PASSWORD' : 'CONTINUE'}
        </button>
        <button onClick={onClose} className='button-neg'>
            CLOSE
        </button>
      </form>

      {message && (
        <div className={message.includes('success') ? 'success' : 'error'}>
          <p>{message}</p>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;